import React from 'react';

import { Button, Col, Input, Row } from 'antd';
import type { InputProps } from 'antd';
import type { ZodSchema } from 'zod';

type TProps = {
    required?: boolean;
    label?: string;
    value?: string | number | number[];
    onChange?: (val: any) => void;
    isEdit?: boolean;
    valueRender?: (value: any, meta: { isValid: boolean; error: string | null }) => React.ReactNode;
    viewRender?: (value: any, meta: { isValid: boolean; error: string | null }) => React.ReactNode;
    inputProps?: InputProps;
    horizontal?: boolean;
    validator?: ZodSchema<any>;
    inputType?: 'input' | 'textarea' | 'button' | 'text';
    buttonProps?: React.ComponentProps<typeof Button>;
};

export const ProfileItem = ({
    label,
    value,
    onChange,
    isEdit = false,
    valueRender,
    viewRender,
    inputProps,
    horizontal = false,
    required = false,
    validator,
    inputType = 'input',
    buttonProps,
}: TProps) => {
    'use memo';
    const validateValue = (value: any, schema?: ZodSchema<any>) => {
        if (!schema) return { isValid: true, error: null };
        const result = schema.safeParse(value);
        if (!result.success) {
            const firstError = result.error.issues?.[0]?.message ?? 'Некорректное значение';
            return { isValid: false, error: firstError };
        }
        return { isValid: true, error: null };
    };

    const validation = validateValue(value, validator);

    const renderLabel = () => {
        if (!label) return null;
        return (
            <p className="label-row">
                {label}
                {required && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
            </p>
        );
    };

    const inputElement = (() => {
        if (inputType === 'textarea') {
            return (
                <Input.TextArea
                    required={required}
                    value={value}
                    onChange={(e) => onChange?.(e.target.value)}
                    status={!validation.isValid ? 'error' : ''}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    {...inputProps}
                />
            );
        }

        if (inputType === 'button') {
            return (
                <Button {...buttonProps} onClick={() => buttonProps?.onClick?.()}>
                    {value || 'Нажми'}
                </Button>
            );
        }
        if (inputType === 'text') {
            return <p>{value}</p>;
        }
        return (
            <Input
                required={required}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                status={!validation.isValid ? 'error' : ''}
                {...inputProps}
            />
        );
    })();

    const content = isEdit ? (
        valueRender ? (
            valueRender(value, validation)
        ) : (
            inputElement
        )
    ) : viewRender ? (
        viewRender(value, validation)
    ) : (
        <p>{value || '—'}</p>
    );

    if (horizontal) {
        return (
            <Row justify="space-between" align="middle" style={{ width: '90%' }}>
                <Col>{renderLabel()}</Col>
                <Col>{content}</Col>
            </Row>
        );
    }

    return (
        <>
            <Row className="label-row">
                <Col>{renderLabel()}</Col>
            </Row>
            <Row className="value-row">
                <Col span={24}>{content}</Col>
            </Row>
        </>
    );
};
